import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import cronstrue from "cronstrue/i18n";
import api from "services/api";
import { useToast } from "../../../hooks/toast";
import "./Cronjobs.css";

const POLL_INTERVAL_MS = 30000;
const ALL_STATES_KEY = "__ALL__";

const STATE_META = {
  active: { label: "Ativo", color: "success" },
  paused: { label: "Pausado", color: "secondary" },
  "legacy-cron": { label: "Legacy", color: "info" },
  "pending-sync": { label: "Sincronizando", color: "warning" },
};

const LAST_RUN_OK = "lastrun-ok";
const LAST_RUN_ERROR = "lastrun-error";

const STATE_FILTERS = [
  { value: "active", label: "Ativos", accent: "#1d8cf8" },
  { value: "paused", label: "Pausados", accent: "#ffb547" },
  { value: LAST_RUN_OK, label: "OK na última rodada", accent: "#00bf9a" },
  { value: LAST_RUN_ERROR, label: "Erro na última rodada", accent: "#fd5d93" },
];

const TIME_WINDOWS = [
  { value: "15m", label: "Próximos 15min", hours: 0.25 },
  { value: "1h", label: "Próxima 1h", hours: 1 },
  { value: "4h", label: "Próximas 4h", hours: 4 },
];

const WEEKDAYS = [
  { value: "1", label: "Seg" },
  { value: "2", label: "Ter" },
  { value: "3", label: "Qua" },
  { value: "4", label: "Qui" },
  { value: "5", label: "Sex" },
  { value: "6", label: "Sáb" },
  { value: "0", label: "Dom" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

// cron (5 campos) -> estado do construtor, ou null se não casa com um
// padrão simples (aí cai no modo "personalizado/avançado").
function cronToBuilder(cron) {
  if (typeof cron !== "string") return null;
  const p = cron.trim().split(/\s+/);
  if (p.length !== 5) return null;
  const [min, hour, dom, mon, dow] = p;
  const simpleField = (f) => /^\d+$/.test(f);

  if (mon === "*" && dom === "*" && dow === "*") {
    const mEvery = min.match(/^\*\/(\d{1,2})$/);
    if (mEvery && hour === "*") {
      return { mode: "everyMinutes", n: Number(mEvery[1]) };
    }
    const hEvery = hour.match(/^\*\/(\d{1,2})$/);
    if (hEvery && simpleField(min)) {
      return { mode: "everyHours", n: Number(hEvery[1]), minute: Number(min) };
    }
    if (simpleField(min) && simpleField(hour)) {
      return { mode: "daily", hour: Number(hour), minute: Number(min) };
    }
  }

  if (mon === "*" && dom === "*" && dow !== "*" && simpleField(min) && simpleField(hour)) {
    const days = dow.split(",").filter((d) => /^[0-6]$/.test(d));
    const rangeMatch = dow.match(/^(\d)-(\d)$/);
    let parsedDays = days;
    if (rangeMatch) {
      parsedDays = [];
      for (let d = Number(rangeMatch[1]); d <= Number(rangeMatch[2]); d += 1) {
        parsedDays.push(String(d));
      }
    }
    if (parsedDays.length) {
      return {
        mode: "weekly",
        hour: Number(hour),
        minute: Number(min),
        days: parsedDays,
      };
    }
  }

  if (mon === "*" && dow === "*" && simpleField(dom) && simpleField(min) && simpleField(hour)) {
    return {
      mode: "monthly",
      day: Number(dom),
      hour: Number(hour),
      minute: Number(min),
    };
  }

  return null;
}

function builderToCron(b) {
  if (!b) return "";
  if (b.mode === "everyMinutes") {
    const n = Math.min(59, Math.max(1, Number(b.n) || 1));
    return `*/${n} * * * *`;
  }
  if (b.mode === "everyHours") {
    const n = Math.min(23, Math.max(1, Number(b.n) || 1));
    const m = Math.min(59, Math.max(0, Number(b.minute) || 0));
    return `${m} */${n} * * *`;
  }
  if (b.mode === "daily") {
    const h = Math.min(23, Math.max(0, Number(b.hour) || 0));
    const m = Math.min(59, Math.max(0, Number(b.minute) || 0));
    return `${m} ${h} * * *`;
  }
  if (b.mode === "weekly") {
    const h = Math.min(23, Math.max(0, Number(b.hour) || 0));
    const m = Math.min(59, Math.max(0, Number(b.minute) || 0));
    const order = ["1", "2", "3", "4", "5", "6", "0"];
    const days = order.filter((d) => (b.days || []).includes(d));
    return `${m} ${h} * * ${days.length ? days.join(",") : "*"}`;
  }
  if (b.mode === "monthly") {
    const d = Math.min(31, Math.max(1, Number(b.day) || 1));
    const h = Math.min(23, Math.max(0, Number(b.hour) || 0));
    const m = Math.min(59, Math.max(0, Number(b.minute) || 0));
    return `${m} ${h} ${d} * *`;
  }
  return "";
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (Math.abs(diffSec) < 60)
    return diffSec >= 0 ? `há ${diffSec}s` : `em ${-diffSec}s`;
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60)
    return diffMin >= 0 ? `há ${diffMin}min` : `em ${-diffMin}min`;
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 48)
    return diffH >= 0 ? `há ${diffH}h` : `em ${-diffH}h`;
  return d.toLocaleString("pt-BR");
}

function humanizeCron(expr) {
  if (!expr) return null;
  try {
    let text = cronstrue.toString(expr, {
      locale: "pt_BR",
      use24HourTimeFormat: true,
      verbose: false,
    });
    text = text
      .replace(/\bda hora\b/gi, "de cada hora")
      .replace(/\bdo dia\b/gi, "de cada dia")
      .replace(/\bdo mês\b/gi, "de cada mês");
    return text.charAt(0).toUpperCase() + text.slice(1);
  } catch (e) {
    return null;
  }
}

function formatDuration(ms) {
  if (ms == null || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function showToast(addToast, type, title, description) {
  addToast({
    place: "tr",
    type,
    title,
    description,
    icon:
      type === "success"
        ? "tim-icons icon-check-2"
        : type === "danger"
        ? "tim-icons icon-alert-circle-exc"
        : "tim-icons icon-bell-55",
  });
}

function StatCard({ label, value, accent, active, onClick }) {
  const className = `cronjobs-stat${active ? " cronjobs-stat--active" : ""}`;
  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        style={{ "--accent": accent }}
        onClick={onClick}
        aria-pressed={active}
        title={active ? "Limpar filtro" : `Filtrar por ${label.toLowerCase()}`}
      >
        <span className="cronjobs-stat__label">{label}</span>
        <strong className="cronjobs-stat__value">{value}</strong>
        <span className="cronjobs-stat__hint">
          {active ? "Filtro ativo · clique para limpar" : "Clique para filtrar"}
        </span>
      </button>
    );
  }
  return (
    <div className={className} style={{ "--accent": accent }}>
      <span className="cronjobs-stat__label">{label}</span>
      <strong className="cronjobs-stat__value">{value}</strong>
    </div>
  );
}

const Cronjobs = () => {
  const { addToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedStack, setExpandedStack] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [activeState, setActiveState] = useState(ALL_STATES_KEY);
  const [timeWindow, setTimeWindow] = useState(null);
  const [escopoFilter, setEscopoFilter] = useState(ALL_STATES_KEY);
  const [embarcadores, setEmbarcadores] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [editTitulo, setEditTitulo] = useState("");
  const [editDescricao, setEditDescricao] = useState("");
  const [editFrequency, setEditFrequency] = useState("");
  const [editBuilder, setEditBuilder] = useState({ mode: "custom" });
  const [editNlText, setEditNlText] = useState("");
  const [editNlLoading, setEditNlLoading] = useState(false);
  const [editNlError, setEditNlError] = useState(null);
  const [editShowRaw, setEditShowRaw] = useState(false);
  const [editHandler, setEditHandler] = useState("");
  const [editType, setEditType] = useState("GET");
  const [editEscopoId, setEditEscopoId] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkPending, setBulkPending] = useState(false);

  const BUILDER_DEFAULTS = {
    everyMinutes: { mode: "everyMinutes", n: 15 },
    everyHours: { mode: "everyHours", n: 1, minute: 0 },
    daily: { mode: "daily", hour: 8, minute: 0 },
    weekly: { mode: "weekly", hour: 8, minute: 0, days: ["1", "2", "3", "4", "5"] },
    monthly: { mode: "monthly", day: 1, hour: 8, minute: 0 },
  };

  const applyBuilder = useCallback((next) => {
    setEditBuilder(next);
    if (next && next.mode !== "custom") {
      setEditFrequency(builderToCron(next));
    }
  }, []);

  const applyRawFrequency = useCallback((value) => {
    setEditFrequency(value);
    const b = cronToBuilder(value);
    setEditBuilder(b || { mode: "custom" });
  }, []);

  const fetchList = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await api.get("/admin/cronjobs");
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Falha ao carregar crons";
        showToast(addToast, "danger", "Erro", msg);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [addToast]
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get("/cadastros-gerais/embarcadores")
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.items || [];
        const normalized = list
          .map((e) => ({
            id: e.id ?? e.Id ?? e.embarcador_id,
            nome: e.fantasia ?? e.razaoSocial ?? e.nome ?? `#${e.id}`,
          }))
          .filter((e) => e.id != null)
          .sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
        setEmbarcadores(normalized);
      })
      .catch(() => {
        if (!cancelled) setEmbarcadores([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchList();
    const id = setInterval(() => fetchList({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchList]);

  const runAction = useCallback(
    async (nome, action, successMsg) => {
      const key = `${nome}:${action}`;
      setActionPending(key);
      try {
        const url = `/admin/cronjobs/${encodeURIComponent(nome)}/${action}`;
        const { data } = await api.post(url);
        showToast(
          addToast,
          "success",
          "OK",
          (data && data.message) || successMsg
        );
        fetchList({ silent: true });
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          `Falha em ${action}`;
        showToast(addToast, "danger", "Erro", msg);
      } finally {
        setActionPending(null);
      }
    },
    [addToast, fetchList]
  );

  const runBulk = useCallback(
    async (kind) => {
      setBulkPending(true);
      try {
        const action = kind === "pause" ? "pause-all" : "resume-all";
        const { data } = await api.post(`/admin/cronjobs/${action}`);
        showToast(
          addToast,
          "success",
          "OK",
          (data && data.message) ||
            (kind === "pause"
              ? "Todos os crons pausados."
              : "Todos os crons retomados.")
        );
        setBulkConfirm(null);
        fetchList({ silent: true });
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Falha na operação em massa";
        showToast(addToast, "danger", "Erro", msg);
      } finally {
        setBulkPending(false);
      }
    },
    [addToast, fetchList]
  );

  const openHistory = useCallback(
    async (nome) => {
      setHistoryTarget(nome);
      setHistoryOpen(true);
      setHistoryItems([]);
      setExpandedStack({});
      setHistoryLoading(true);
      try {
        const { data } = await api.get(
          `/admin/cronjobs/${encodeURIComponent(nome)}/history?limit=20`
        );
        setHistoryItems(Array.isArray(data.items) ? data.items : []);
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Falha ao carregar histórico";
        showToast(addToast, "danger", "Erro", msg);
      } finally {
        setHistoryLoading(false);
      }
    },
    [addToast]
  );

  const closeHistory = useCallback(() => {
    setHistoryOpen(false);
    setHistoryTarget(null);
    setHistoryItems([]);
  }, []);

  const openEdit = useCallback((it) => {
    setEditTarget(it);
    setEditTitulo(it.titulo || "");
    setEditDescricao(it.descricao || "");
    const freq = it.frequency || "";
    setEditFrequency(freq);
    const b = cronToBuilder(freq);
    setEditBuilder(b || { mode: "custom" });
    setEditShowRaw(!b);
    setEditNlText("");
    setEditNlError(null);
    setEditHandler(it.handler || "");
    setEditType((it.type || "GET").toUpperCase());
    setEditEscopoId(
      it.escopoEmbarcadorId != null ? String(it.escopoEmbarcadorId) : ""
    );
    setEditError(null);
  }, []);

  const resetEdit = useCallback(() => {
    setEditTarget(null);
    setEditTitulo("");
    setEditDescricao("");
    setEditFrequency("");
    setEditBuilder({ mode: "custom" });
    setEditNlText("");
    setEditNlError(null);
    setEditShowRaw(false);
    setEditHandler("");
    setEditType("GET");
    setEditEscopoId("");
    setEditError(null);
  }, []);

  const closeEdit = useCallback(() => {
    if (editSaving) return;
    resetEdit();
  }, [editSaving, resetEdit]);

  const editFrequencyPreview = useMemo(
    () => humanizeCron(editFrequency.trim()),
    [editFrequency]
  );

  const interpretNl = useCallback(async () => {
    const text = editNlText.trim();
    if (!text) return;
    setEditNlLoading(true);
    setEditNlError(null);
    try {
      const { data } = await api.post("/admin/cronjobs/nl-to-cron", { text });
      const cron = data && data.cron;
      if (!cron || !humanizeCron(cron)) {
        setEditNlError(
          (data && data.message) ||
            "Não consegui transformar isso numa agenda válida. Tente reescrever."
        );
        return;
      }
      const b = cronToBuilder(cron);
      setEditFrequency(cron);
      setEditBuilder(b || { mode: "custom" });
      setEditShowRaw(!b);
    } catch (err) {
      const body = err && err.response && err.response.data;
      const msg =
        (body && (body.message || body.error)) ||
        (err && err.message) ||
        "Falha ao interpretar o texto";
      setEditNlError(msg);
    } finally {
      setEditNlLoading(false);
    }
  }, [editNlText]);

  const saveEdit = useCallback(async () => {
    if (!editTarget) return;
    const tituloTrim = editTitulo.trim();
    const descricaoTrim = editDescricao.trim();
    const frequencyTrim = editFrequency.trim();
    const handlerTrim = editHandler.trim();
    if (!descricaoTrim) {
      setEditError("A descrição não pode ficar vazia.");
      return;
    }
    if (
      !frequencyTrim ||
      !editFrequencyPreview ||
      frequencyTrim.split(/\s+/).length !== 5
    ) {
      setEditError(
        "A expressão cron é inválida. Use exatamente 5 campos, ex: 43 * * * *"
      );
      return;
    }
    if (!handlerTrim || !handlerTrim.startsWith("/")) {
      setEditError('O handler deve começar com "/".');
      return;
    }
    let escopoId = null;
    let escopoNome = null;
    if (editEscopoId) {
      const emb = embarcadores.find(
        (e) => String(e.id) === String(editEscopoId)
      );
      if (emb) {
        escopoId = Number(emb.id);
        escopoNome = emb.nome;
      } else if (
        editTarget.escopoEmbarcadorId != null &&
        String(editTarget.escopoEmbarcadorId) === String(editEscopoId)
      ) {
        // Lista de embarcadores indisponível, mas o escopo não mudou —
        // preserva o que já estava gravado em vez de bloquear o save.
        escopoId = Number(editTarget.escopoEmbarcadorId);
        escopoNome = editTarget.escopoEmbarcadorNome || `#${escopoId}`;
      } else {
        setEditError("Embarcador de escopo inválido. Selecione novamente.");
        return;
      }
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const url = `/admin/cronjobs/${encodeURIComponent(editTarget.nome)}`;
      await api.patch(url, {
        titulo: tituloTrim || null,
        descricao: descricaoTrim,
        frequency: frequencyTrim,
        handler: handlerTrim,
        type: editType,
        // Pós-migração (PO1-1982) todo cron roda via BullMQ; salvar um doc
        // legado antigo migra ele junto.
        runMode: "bullmq",
        escopoEmbarcadorId: escopoId,
        escopoEmbarcadorNome: escopoNome,
      });
      showToast(addToast, "success", "OK", "Cron atualizado.");
      resetEdit();
      fetchList({ silent: true });
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        err.message ||
        "Falha ao atualizar";
      setEditError(msg);
      showToast(addToast, "danger", "Erro", msg);
    } finally {
      setEditSaving(false);
    }
  }, [
    addToast,
    editTarget,
    editTitulo,
    editDescricao,
    editFrequency,
    editFrequencyPreview,
    editHandler,
    editType,
    editEscopoId,
    embarcadores,
    fetchList,
    resetEdit,
  ]);

  const stateCounts = useMemo(() => {
    const c = { active: 0, paused: 0, [LAST_RUN_OK]: 0, [LAST_RUN_ERROR]: 0 };
    for (const it of items) {
      if (c[it.state] != null) c[it.state] += 1;
      if (it.lastRun) {
        if (it.lastRun.status === "ok") c[LAST_RUN_OK] += 1;
        else c[LAST_RUN_ERROR] += 1;
      }
    }
    return c;
  }, [items]);

  const ativoCount = useMemo(
    () => items.filter((it) => it.ativo).length,
    [items]
  );
  const pausadoCount = useMemo(
    () => items.filter((it) => !it.ativo).length,
    [items]
  );

  const windowCounts = useMemo(() => {
    const now = Date.now();
    return TIME_WINDOWS.reduce((acc, w) => {
      const horizon = now + w.hours * 3600 * 1000;
      acc[w.value] = items.filter((it) => {
        if (it.state !== "active" || !it.nextRunAt) return false;
        const t = new Date(it.nextRunAt).getTime();
        return !Number.isNaN(t) && t <= horizon;
      }).length;
      return acc;
    }, {});
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const windowConfig = timeWindow
      ? TIME_WINDOWS.find((w) => w.value === timeWindow)
      : null;
    const horizon = windowConfig
      ? Date.now() + windowConfig.hours * 3600 * 1000
      : null;
    const filtered = items.filter((it) => {
      if (activeState === LAST_RUN_OK) {
        if (!it.lastRun || it.lastRun.status !== "ok") return false;
      } else if (activeState === LAST_RUN_ERROR) {
        if (!it.lastRun || it.lastRun.status === "ok") return false;
      } else if (activeState !== ALL_STATES_KEY && it.state !== activeState) {
        return false;
      }
      if (horizon != null) {
        if (it.state !== "active" || !it.nextRunAt) return false;
        const t = new Date(it.nextRunAt).getTime();
        if (Number.isNaN(t) || t > horizon) return false;
      }
      if (escopoFilter !== ALL_STATES_KEY) {
        if (escopoFilter === "__GERAL__") {
          if (it.escopoEmbarcadorId != null) return false;
        } else if (
          String(it.escopoEmbarcadorId) !== String(escopoFilter)
        ) {
          return false;
        }
      }
      if (!term) return true;
      const haystack = [it.nome, it.titulo, it.descricao, it.frequency]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
    return filtered.sort((a, b) => {
      const ta = a.nextRunAt ? new Date(a.nextRunAt).getTime() : NaN;
      const tb = b.nextRunAt ? new Date(b.nextRunAt).getTime() : NaN;
      const aValid = !Number.isNaN(ta);
      const bValid = !Number.isNaN(tb);
      if (aValid && bValid) return ta - tb;
      if (aValid) return -1;
      if (bValid) return 1;
      return (a.nome || "").localeCompare(b.nome || "", "pt-BR");
    });
  }, [items, searchTerm, activeState, timeWindow, escopoFilter]);

  const escopoOptions = useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      if (it.escopoEmbarcadorId != null && !seen.has(it.escopoEmbarcadorId)) {
        seen.set(
          it.escopoEmbarcadorId,
          it.escopoEmbarcadorNome || `#${it.escopoEmbarcadorId}`
        );
      }
    }
    return Array.from(seen.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
  }, [items]);

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card className="cronjobs-hero">
            <CardBody>
              <div className="cronjobs-hero__top">
                <div className="cronjobs-hero__intro">
                  <span className="cronjobs-hero__eyebrow">
                    Orquestração & jobs
                  </span>
                  <h3 className="cronjobs-hero__title">Cron Jobs</h3>
                  <p className="cronjobs-hero__description">
                    Painel de controle dos agendamentos migrados para BullMQ
                    (PO1-1982). Acompanhe próxima execução, histórico recente e
                    intervenha pontualmente sem precisar abrir o servidor.
                  </p>
                  <div className="cronjobs-hero__context">
                    <Badge color="info" pill>
                      Admin
                    </Badge>
                    <span>Auto-refresh a cada 30s</span>
                    {loading ? <Spinner size="sm" color="primary" /> : null}
                  </div>
                </div>

                <div className="cronjobs-hero__actions">
                  <div className="cronjobs-hero__buttons">
                    <Button
                      color="primary"
                      onClick={() => fetchList()}
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <i className="tim-icons icon-refresh-01 mr-1" />
                          Atualizar
                        </>
                      )}
                    </Button>
                    {pausadoCount > 0 ? (
                      <Button
                        color="success"
                        outline
                        onClick={() => setBulkConfirm("resume")}
                        disabled={loading || bulkPending}
                        title="Retomar todos os crons"
                      >
                        <i className="tim-icons icon-button-power mr-1" />
                        Retomar TUDO
                      </Button>
                    ) : null}
                    <Button
                      color="danger"
                      onClick={() => setBulkConfirm("pause")}
                      disabled={loading || bulkPending || ativoCount === 0}
                      title="Pausar imediatamente todos os crons"
                    >
                      <i className="tim-icons icon-button-pause mr-1" />
                      Pausar TUDO
                    </Button>
                  </div>
                </div>
              </div>

              <Row className="mt-4">
                {STATE_FILTERS.map((f) => (
                  <Col md={6} lg={3} key={f.value}>
                    <StatCard
                      label={f.label}
                      value={stateCounts[f.value] || 0}
                      accent={f.accent}
                      active={activeState === f.value}
                      onClick={() =>
                        setActiveState((current) =>
                          current === f.value ? ALL_STATES_KEY : f.value
                        )
                      }
                    />
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="cronjobs-toolbar">
            <CardBody>
              <Row className="align-items-end">
                <Col lg={5}>
                  <FormGroup className="mb-0">
                    <Label className="cronjobs-toolbar__label">
                      Buscar crons e agendamentos
                    </Label>
                    <Input
                      type="text"
                      value={searchTerm}
                      placeholder="Nome, título, descrição ou padrão de agendamento"
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col lg={3}>
                  <FormGroup className="mb-0">
                    <Label className="cronjobs-toolbar__label">Escopo</Label>
                    <Input
                      type="select"
                      value={escopoFilter}
                      onChange={(event) => setEscopoFilter(event.target.value)}
                    >
                      <option value={ALL_STATES_KEY}>Todos</option>
                      <option value="__GERAL__">Geral</option>
                      {escopoOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.nome}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col lg={4}>
                  <div className="cronjobs-toolbar__actions">
                    {activeState !== ALL_STATES_KEY ? (
                      <span className="cronjobs-toolbar__chip">
                        <i className="tim-icons icon-bullet-list-67" />
                        {STATE_FILTERS.find((f) => f.value === activeState)?.label}
                      </span>
                    ) : null}
                    {timeWindow ? (
                      <span className="cronjobs-toolbar__chip">
                        <i className="tim-icons icon-time-alarm" />
                        {TIME_WINDOWS.find((w) => w.value === timeWindow)?.label}
                      </span>
                    ) : null}
                    {escopoFilter !== ALL_STATES_KEY ? (
                      <span className="cronjobs-toolbar__chip">
                        <i className="tim-icons icon-single-02" />
                        {escopoFilter === "__GERAL__"
                          ? "Geral"
                          : escopoOptions.find(
                              (o) => String(o.id) === String(escopoFilter)
                            )?.nome || "Escopo"}
                      </span>
                    ) : null}
                    {activeState !== ALL_STATES_KEY ||
                    timeWindow ||
                    searchTerm ||
                    escopoFilter !== ALL_STATES_KEY ? (
                      <Button
                        color="default"
                        outline
                        size="sm"
                        onClick={() => {
                          setActiveState(ALL_STATES_KEY);
                          setTimeWindow(null);
                          setSearchTerm("");
                          setEscopoFilter(ALL_STATES_KEY);
                        }}
                      >
                        Limpar filtros
                      </Button>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <div className="cronjobs-window">
                <span className="cronjobs-toolbar__label cronjobs-window__label">
                  Próxima execução
                </span>
                <div className="cronjobs-window__pills">
                  {TIME_WINDOWS.map((w) => {
                    const count = windowCounts[w.value] || 0;
                    const active = timeWindow === w.value;
                    return (
                      <button
                        key={w.value}
                        type="button"
                        className={`cronjobs-window-pill${
                          active ? " cronjobs-window-pill--active" : ""
                        }${count === 0 && !active ? " cronjobs-window-pill--muted" : ""}`}
                        onClick={() =>
                          setTimeWindow((current) =>
                            current === w.value ? null : w.value
                          )
                        }
                        aria-pressed={active}
                        title={
                          active
                            ? "Clique para limpar este filtro"
                            : `Filtrar pelas que vão rodar nas ${w.label.toLowerCase()}`
                        }
                      >
                        <i className="tim-icons icon-watch-time" />
                        <span>{w.label}</span>
                        <strong>{count}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="cronjobs-table-card">
            <CardHeader className="cronjobs-table-card__header">
              <div>
                <CardTitle tag="h5" className="mb-1">
                  Agendamentos
                </CardTitle>
                <small>
                  {(() => {
                    const parts = [];
                    if (activeState !== ALL_STATES_KEY) {
                      parts.push(
                        STATE_FILTERS.find((f) => f.value === activeState)?.label ||
                          activeState
                      );
                    }
                    if (timeWindow) {
                      parts.push(
                        TIME_WINDOWS.find((w) => w.value === timeWindow)?.label
                      );
                    }
                    return parts.length
                      ? `Recorte: ${parts.join(" · ")}`
                      : "Visão geral de todos os crons e agendamentos cadastrados";
                  })()}
                </small>
              </div>
              <Badge color="default" pill>
                {filteredItems.length}
              </Badge>
            </CardHeader>
            <CardBody>
              {loading && items.length === 0 ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="cronjobs-empty">
                  <h5>Nenhum cron ou agendamento no recorte atual</h5>
                  <p>
                    Ajuste o filtro de estado ou limpe o termo de busca para
                    voltar a enxergar os agendamentos.
                  </p>
                </div>
              ) : (
                <Table responsive hover className="cronjobs-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Escopo</th>
                      <th>Padrão</th>
                      <th>Estado</th>
                      <th>Última execução</th>
                      <th>Duração</th>
                      <th>Próxima</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => {
                      const st = STATE_META[it.state] || {
                        label: it.state,
                        color: "default",
                      };
                      const last = it.lastRun;
                      const isPending =
                        actionPending && actionPending.startsWith(`${it.nome}:`);
                      const isLegacy = it.state === "legacy-cron";
                      return (
                        <tr key={it.nome}>
                          <td>
                            {it.titulo ? (
                              <div className="cronjobs-name">{it.titulo}</div>
                            ) : (
                              <div className="cronjobs-name cronjobs-name--empty">
                                Sem título
                              </div>
                            )}
                            {it.descricao ? (
                              <div className="cronjobs-desc">
                                {it.descricao}
                              </div>
                            ) : null}
                            <code
                              className="cronjobs-id"
                              title="Identificador técnico (nome)"
                            >
                              {it.nome}
                            </code>
                          </td>
                          <td>
                            {it.escopoEmbarcadorId != null ? (
                              <Badge color="info" pill>
                                {it.escopoEmbarcadorNome ||
                                  `#${it.escopoEmbarcadorId}`}
                              </Badge>
                            ) : (
                              <Badge color="secondary" pill>
                                Geral
                              </Badge>
                            )}
                          </td>
                          <td>
                            {(() => {
                              const human = humanizeCron(it.frequency);
                              if (!human) {
                                return (
                                  <code className="cronjobs-pattern">
                                    {it.frequency}
                                  </code>
                                );
                              }
                              return (
                                <div className="cronjobs-cron">
                                  <span className="cronjobs-cron__human">
                                    {human}
                                  </span>
                                  <span
                                    className="cronjobs-cron__info"
                                    data-tooltip={it.frequency}
                                    aria-label={`Expressão cron: ${it.frequency}`}
                                    tabIndex={0}
                                  >
                                    i
                                  </span>
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <Badge color={st.color} pill>
                              {st.label}
                            </Badge>
                          </td>
                          <td>
                            {last ? (
                              <>
                                <div>{relativeTime(last.ranAt)}</div>
                                <div className="cronjobs-last-status">
                                  {last.status === "ok" ? (
                                    <span className="ok">
                                      OK · {last.httpStatus ?? "—"}
                                    </span>
                                  ) : (
                                    <span className="fail">
                                      Falhou · {last.error || "sem detalhe"}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <span className="cronjobs-muted">—</span>
                            )}
                          </td>
                          <td>
                            {last ? formatDuration(last.durationMs) : "—"}
                          </td>
                          <td>
                            {it.state === "active" && it.nextRunAt
                              ? relativeTime(it.nextRunAt)
                              : "—"}
                          </td>
                          <td className="text-right">
                            <div className="cronjobs-actions">
                              <Button
                                color="primary"
                                size="sm"
                                outline
                                className="cronjobs-action-btn"
                                disabled={isPending || isLegacy}
                                onClick={() =>
                                  runAction(
                                    it.nome,
                                    "run-now",
                                    "Job manual enfileirado"
                                  )
                                }
                                title="Executar agora"
                              >
                                <i className="tim-icons icon-spaceship" />
                              </Button>
                              {it.ativo ? (
                                <Button
                                  color="warning"
                                  size="sm"
                                  outline
                                  className="cronjobs-action-btn"
                                  disabled={isPending}
                                  onClick={() =>
                                    runAction(it.nome, "pause", "Pausado")
                                  }
                                  title="Pausar agendamento"
                                >
                                  <i className="tim-icons icon-button-pause" />
                                </Button>
                              ) : (
                                <Button
                                  color="success"
                                  size="sm"
                                  outline
                                  className="cronjobs-action-btn"
                                  disabled={isPending}
                                  onClick={() =>
                                    runAction(it.nome, "resume", "Retomado")
                                  }
                                  title="Retomar agendamento"
                                >
                                  <i className="tim-icons icon-button-power" />
                                </Button>
                              )}
                              <Button
                                color="default"
                                size="sm"
                                outline
                                className="cronjobs-action-btn"
                                onClick={() => openEdit(it)}
                                title="Editar título e descrição"
                              >
                                <i className="tim-icons icon-pencil" />
                              </Button>
                              <Button
                                color="info"
                                size="sm"
                                outline
                                className="cronjobs-action-btn"
                                onClick={() => openHistory(it.nome)}
                                title="Ver histórico de execuções"
                              >
                                <i className="tim-icons icon-paper" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={historyOpen}
        toggle={closeHistory}
        size="lg"
        fade={false}
        modalClassName="modal-align-top cronjobs-modal"
      >
        <div className="cronjobs-modal__header">
          <div>
            <div className="cronjobs-modal__eyebrow">
              Histórico de execuções
            </div>
            <h4 className="cronjobs-modal__title">{historyTarget}</h4>
          </div>
          <button
            type="button"
            className="cronjobs-modal__close"
            onClick={closeHistory}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <ModalBody>
          {historyLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : historyItems.length === 0 ? (
            <div className="cronjobs-empty">
              <h5>Sem execuções registradas</h5>
              <p>
                Esse cron ainda não rodou ou o histórico foi expurgado. Tente
                novamente após a próxima execução.
              </p>
            </div>
          ) : (
            <Table responsive className="cronjobs-history-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Status</th>
                  <th>HTTP</th>
                  <th>Duração</th>
                  <th>Tentativas</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {historyItems.map((h) => {
                  const stackVisible = expandedStack[h.jobId];
                  return (
                    <React.Fragment key={h.jobId}>
                      <tr>
                        <td>
                          <div>{relativeTime(h.ranAt)}</div>
                          <div className="cronjobs-muted cronjobs-small">
                            {h.ranAt
                              ? new Date(h.ranAt).toLocaleString("pt-BR")
                              : ""}
                          </div>
                        </td>
                        <td>
                          {h.timedOut ? (
                            // PO1-2749: h.status="failed" + h.httpStatus=500
                            // aqui é o TIMEOUT DO PAINEL (~120s), não uma
                            // falha real — o job costuma seguir rodando no
                            // servidor e terminar com sucesso depois. Antes
                            // isso aparecia como "Falhou · 500", indistinguível
                            // de um erro real, e levava o operador a disparar
                            // de novo manualmente (execução concorrente).
                            <Badge color="warning" pill>
                              Em andamento
                            </Badge>
                          ) : h.status === "ok" ? (
                            <Badge color="success" pill>
                              OK
                            </Badge>
                          ) : (
                            <Badge color="danger" pill>
                              Falhou
                            </Badge>
                          )}
                        </td>
                        <td>
                          {h.timedOut
                            ? "— (timeout do painel)"
                            : h.httpStatus ?? "—"}
                        </td>
                        <td>{formatDuration(h.durationMs)}</td>
                        <td>{h.attemptsMade ?? "—"}</td>
                        <td>
                          {h.error ? (
                            <>
                              <span className="cronjobs-error-msg">
                                {h.error}
                              </span>
                              {h.stacktrace ? (
                                <Button
                                  size="sm"
                                  color="link"
                                  className="cronjobs-stack-toggle"
                                  onClick={() =>
                                    setExpandedStack((prev) => ({
                                      ...prev,
                                      [h.jobId]: !prev[h.jobId],
                                    }))
                                  }
                                >
                                  {stackVisible ? "ocultar stack" : "ver stack"}
                                </Button>
                              ) : null}
                            </>
                          ) : (
                            <span className="cronjobs-muted">—</span>
                          )}
                        </td>
                      </tr>
                      {stackVisible && h.stacktrace ? (
                        <tr className="cronjobs-stack-row">
                          <td colSpan={6}>
                            <pre className="cronjobs-stack">{h.stacktrace}</pre>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="default" outline onClick={closeHistory}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(editTarget)}
        toggle={closeEdit}
        fade={false}
        modalClassName="modal-align-top cronjobs-modal"
      >
        <div className="cronjobs-modal__header">
          <div>
            <div className="cronjobs-modal__eyebrow">Editar cron</div>
            <h4 className="cronjobs-modal__title">
              {editTarget ? editTarget.nome : ""}
            </h4>
          </div>
          <button
            type="button"
            className="cronjobs-modal__close"
            onClick={closeEdit}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <ModalBody>
          {editError ? (
            <div className="cronjobs-edit-error">{editError}</div>
          ) : null}
          <FormGroup>
            <Label className="cronjobs-toolbar__label">Título</Label>
            <Input
              type="text"
              value={editTitulo}
              maxLength={120}
              placeholder="Nome curto e legível, ex: Divergências CTe Tegma"
              onChange={(event) => setEditTitulo(event.target.value)}
              disabled={editSaving}
            />
            <small className="cronjobs-edit-helper">
              Aparece como nome principal na lista. Deixe vazio para usar a
              descrição.
            </small>
          </FormGroup>
          <FormGroup>
            <Label className="cronjobs-toolbar__label">Descrição</Label>
            <Input
              type="textarea"
              rows={3}
              value={editDescricao}
              maxLength={500}
              placeholder="Explique o que o cron faz e qual o impacto operacional."
              onChange={(event) => setEditDescricao(event.target.value)}
              disabled={editSaving}
            />
          </FormGroup>
          <FormGroup>
            <Label className="cronjobs-toolbar__label">Agendamento</Label>

            <div className="cronjobs-nl">
              <Input
                type="textarea"
                rows={2}
                value={editNlText}
                placeholder="Descreva em palavras, ex: todo dia às 8h da manhã, menos no fim de semana"
                onChange={(event) => setEditNlText(event.target.value)}
                disabled={editSaving || editNlLoading}
              />
              <Button
                color="info"
                outline
                onClick={interpretNl}
                disabled={editSaving || editNlLoading || !editNlText.trim()}
              >
                {editNlLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="tim-icons icon-spaceship mr-1" />
                    Interpretar
                  </>
                )}
              </Button>
            </div>
            {editNlError ? (
              <div className="cronjobs-edit-error">{editNlError}</div>
            ) : null}

            <Row className="mt-2">
              <Col md={5}>
                <Label className="cronjobs-edit-helper mb-1">Frequência</Label>
                <Input
                  type="select"
                  value={editBuilder.mode}
                  disabled={editSaving}
                  onChange={(event) => {
                    const m = event.target.value;
                    if (m === "custom") {
                      setEditBuilder({ mode: "custom" });
                      setEditShowRaw(true);
                    } else {
                      applyBuilder(BUILDER_DEFAULTS[m]);
                    }
                  }}
                >
                  <option value="everyMinutes">A cada N minutos</option>
                  <option value="everyHours">A cada N horas</option>
                  <option value="daily">Todo dia (horário)</option>
                  <option value="weekly">Dias da semana</option>
                  <option value="monthly">Dia do mês</option>
                  <option value="custom">Personalizado (avançado)</option>
                </Input>
              </Col>
              <Col md={7}>
                {editBuilder.mode === "everyMinutes" ? (
                  <>
                    <Label className="cronjobs-edit-helper mb-1">
                      A cada quantos minutos
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={59}
                      value={editBuilder.n ?? 15}
                      disabled={editSaving}
                      onChange={(e) =>
                        applyBuilder({
                          ...editBuilder,
                          n: Number(e.target.value),
                        })
                      }
                    />
                  </>
                ) : null}

                {editBuilder.mode === "everyHours" ? (
                  <Row>
                    <Col xs={6}>
                      <Label className="cronjobs-edit-helper mb-1">
                        A cada N horas
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        max={23}
                        value={editBuilder.n ?? 1}
                        disabled={editSaving}
                        onChange={(e) =>
                          applyBuilder({
                            ...editBuilder,
                            n: Number(e.target.value),
                          })
                        }
                      />
                    </Col>
                    <Col xs={6}>
                      <Label className="cronjobs-edit-helper mb-1">
                        No minuto
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        value={editBuilder.minute ?? 0}
                        disabled={editSaving}
                        onChange={(e) =>
                          applyBuilder({
                            ...editBuilder,
                            minute: Number(e.target.value),
                          })
                        }
                      />
                    </Col>
                  </Row>
                ) : null}

                {editBuilder.mode === "daily" ||
                editBuilder.mode === "weekly" ? (
                  <>
                    <Label className="cronjobs-edit-helper mb-1">Horário</Label>
                    <Input
                      type="time"
                      value={`${pad2(editBuilder.hour ?? 8)}:${pad2(
                        editBuilder.minute ?? 0
                      )}`}
                      disabled={editSaving}
                      onChange={(e) => {
                        const [h, m] = (e.target.value || "08:00").split(":");
                        applyBuilder({
                          ...editBuilder,
                          hour: Number(h),
                          minute: Number(m),
                        });
                      }}
                    />
                  </>
                ) : null}

                {editBuilder.mode === "monthly" ? (
                  <Row>
                    <Col xs={5}>
                      <Label className="cronjobs-edit-helper mb-1">
                        Dia do mês
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        value={editBuilder.day ?? 1}
                        disabled={editSaving}
                        onChange={(e) =>
                          applyBuilder({
                            ...editBuilder,
                            day: Number(e.target.value),
                          })
                        }
                      />
                    </Col>
                    <Col xs={7}>
                      <Label className="cronjobs-edit-helper mb-1">
                        Horário
                      </Label>
                      <Input
                        type="time"
                        value={`${pad2(editBuilder.hour ?? 8)}:${pad2(
                          editBuilder.minute ?? 0
                        )}`}
                        disabled={editSaving}
                        onChange={(e) => {
                          const [h, m] = (e.target.value || "08:00").split(
                            ":"
                          );
                          applyBuilder({
                            ...editBuilder,
                            hour: Number(h),
                            minute: Number(m),
                          });
                        }}
                      />
                    </Col>
                  </Row>
                ) : null}

                {editBuilder.mode === "custom" ? (
                  <small className="cronjobs-edit-helper">
                    Modo livre — edite a expressão cron no campo avançado
                    abaixo.
                  </small>
                ) : null}
              </Col>
            </Row>

            {editBuilder.mode === "weekly" ? (
              <div className="cronjobs-weekdays">
                {WEEKDAYS.map((d) => {
                  const on = (editBuilder.days || []).includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      className={`cronjobs-weekday${
                        on ? " cronjobs-weekday--on" : ""
                      }`}
                      disabled={editSaving}
                      onClick={() => {
                        const set = new Set(editBuilder.days || []);
                        if (set.has(d.value)) set.delete(d.value);
                        else set.add(d.value);
                        applyBuilder({
                          ...editBuilder,
                          days: Array.from(set),
                        });
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="cronjobs-weekday cronjobs-weekday--preset"
                  disabled={editSaving}
                  onClick={() =>
                    applyBuilder({
                      ...editBuilder,
                      days: ["1", "2", "3", "4", "5"],
                    })
                  }
                >
                  Dias úteis
                </button>
              </div>
            ) : null}

            <div
              className={`cronjobs-sched-preview${
                editFrequency.trim() && !editFrequencyPreview
                  ? " cronjobs-sched-preview--err"
                  : ""
              }`}
            >
              {editFrequency.trim() ? (
                editFrequencyPreview ? (
                  <>
                    <i className="tim-icons icon-check-2 mr-1" />
                    Vai rodar: <strong>{editFrequencyPreview}</strong>
                  </>
                ) : (
                  "Expressão inválida — ajuste acima ou no campo avançado."
                )
              ) : (
                "Configure a frequência acima para ver o resumo."
              )}
            </div>

            <button
              type="button"
              className="cronjobs-raw-toggle"
              onClick={() => setEditShowRaw((v) => !v)}
            >
              {editShowRaw
                ? "Ocultar expressão cron"
                : "Avançado — ver/editar expressão cron"}
            </button>
            {editShowRaw ? (
              <Input
                type="text"
                className={`mt-1 ${
                  editFrequency.trim() && !editFrequencyPreview
                    ? "cronjobs-input-invalid"
                    : ""
                }`}
                value={editFrequency}
                placeholder="5 campos: minuto hora diaMes mes diaSemana — ex: 43 * * * *"
                onChange={(event) => applyRawFrequency(event.target.value)}
                disabled={editSaving}
              />
            ) : null}
          </FormGroup>
          <Row>
            <Col md={7}>
              <FormGroup>
                <Label className="cronjobs-toolbar__label">Handler</Label>
                <Input
                  type="text"
                  value={editHandler}
                  placeholder="/process/divergencia/cte/atualizar/49"
                  onChange={(event) => setEditHandler(event.target.value)}
                  disabled={editSaving}
                />
              </FormGroup>
            </Col>
            <Col md={5}>
              <FormGroup>
                <Label className="cronjobs-toolbar__label">Método</Label>
                <Input
                  type="select"
                  value={editType}
                  onChange={(event) => setEditType(event.target.value)}
                  disabled={editSaving}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-0">
                <Label className="cronjobs-toolbar__label">Escopo</Label>
                <Input
                  type="select"
                  value={editEscopoId}
                  onChange={(event) => setEditEscopoId(event.target.value)}
                  disabled={editSaving || embarcadores.length === 0}
                >
                  <option value="">Geral (todos)</option>
                  {embarcadores.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </Input>
                <small className="cronjobs-edit-helper">
                  {embarcadores.length === 0
                    ? "Lista de embarcadores indisponível — não foi possível carregar."
                    : "Geral = roda para todos. Ou vincule a um cliente específico."}
                </small>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="default"
            outline
            onClick={closeEdit}
            disabled={editSaving}
          >
            Cancelar
          </Button>
          <Button color="primary" onClick={saveEdit} disabled={editSaving}>
            {editSaving ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(bulkConfirm)}
        toggle={() => !bulkPending && setBulkConfirm(null)}
        fade={false}
        modalClassName="modal-align-top cronjobs-modal"
      >
        <div className="cronjobs-modal__header">
          <div>
            <div className="cronjobs-modal__eyebrow">
              {bulkConfirm === "pause" ? "Parada de emergência" : "Retomada em massa"}
            </div>
            <h4 className="cronjobs-modal__title">
              {bulkConfirm === "pause"
                ? "Pausar TODOS os crons?"
                : "Retomar TODOS os crons?"}
            </h4>
          </div>
          <button
            type="button"
            className="cronjobs-modal__close"
            onClick={() => !bulkPending && setBulkConfirm(null)}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <ModalBody>
          {bulkConfirm === "pause" ? (
            <p className="cronjobs-modal__copy">
              Isso vai pausar imediatamente os{" "}
              <strong>{ativoCount}</strong> cron(s) ativos. Nenhum agendamento
              dispara até alguém retomar. Efeito imediato (scheduler legado +
              BullMQ).
            </p>
          ) : (
            <>
              <p className="cronjobs-modal__copy">
                Isso vai reativar os <strong>{pausadoCount}</strong> cron(s)
                pausados.
              </p>
              <div className="cronjobs-edit-error">
                Atenção: retoma <strong>todos</strong> — inclusive os que
                estavam pausados de propósito antes da parada. Se só alguns
                devem voltar, retome um a um pela lista.
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="default"
            outline
            onClick={() => setBulkConfirm(null)}
            disabled={bulkPending}
          >
            Cancelar
          </Button>
          <Button
            color={bulkConfirm === "pause" ? "danger" : "success"}
            onClick={() => runBulk(bulkConfirm)}
            disabled={bulkPending}
          >
            {bulkPending ? (
              <Spinner size="sm" />
            ) : bulkConfirm === "pause" ? (
              "Pausar tudo"
            ) : (
              "Retomar tudo"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Cronjobs;
